import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { Option } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import DataTable, { DataTableProps } from '@repo/ui/data-table'
import { EyeOpenIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { ReactNode } from 'react'

import { Paths } from '../../../lib/constants/path'
import { TransportTypeEnum, TripTypeEnum } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripStepTypeEnum } from '../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { DepartureTimeTripProvider } from '../../../lib/types/models/trip-booking/departure-time-trip-provider'
import {
  TripBookingLabelDetailsOptions,
  TripBookingProperties
} from '../../../lib/types/models/trip-booking/properties.trip-booking'
import { TripDateProperties } from '../../../lib/types/models/trip-booking/properties.trip-date'
import { TripStepProperties } from '../../../lib/types/models/trip-booking/properties.trip-step'
import { TripBookingTypes } from '../../../lib/types/models/trip-booking/trip-booking'
import { TripStepTypes } from '../../../lib/types/models/trip-booking/trip-step'
import { TripPlaceToCenterRequestPartial } from '../../../lib/types/models/trip-place-to-center-request/trip-place-to-center-request'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { FormControl, FormItem } from '../../elements/forms/form'
import ControlledRadioInput from '../../elements/inputs/controlled-radio-input'
import { TripDateDirectionalityBadge } from '../trip-dates/trip-dates.ui'

/**
 * Button to view a tripbooking, it will navigate to the tripbooking update page
 * @param tripbookingId - The id of the tripbooking to view
 * @returns A button to view a trip booking
 */
export function ViewTripBookingButton({
  tripBookingId,
  isActive = false
}: {
  tripBookingId: number
  isActive?: boolean
}) {
  const path = Paths.TRIP_BOOKING_UPDATE_PAGE.replace(':id', tripBookingId.toString())
  return (
    <div
      className={cn(
        'flex items-center justify-center w-6 h-6 min-h-6 min-w-6 rounded-full bg-primary-light cursor-pointer',
        { 'border border-primary-light-active': isActive }
      )}
      onClick={(e) => {
        e.stopPropagation()
        window.open(path, '_blank')
      }}
    >
      <EyeOpenIcon fill={tailwindColors.primary.DEFAULT} className={'w-5 h-5'} />
    </div>
  )
}

export const tripBookingTypeMapping: Record<TripBookingType, Option<TailwindColor>> = {
  main: { label: 'Mère', value: tailwindColors.orchid.DEFAULT },
  secondary: { label: 'Fille', value: tailwindColors.yellow.DEFAULT }
}

type ControlledTripStepOptionProps = {
  tripStep: TripStepTypes.Light
  index: number
  selectedTripStepId?: number
  displayInput?: boolean
  onChange?: (tripStepId: number) => void
}

type TripBookingType = 'main' | 'secondary'

type TripBookingItemProps = {
  type: TripBookingType
  tripBooking: TripBookingTypes.Partial
  directionality?: TripStepTypeEnum
  options?: TripBookingLabelDetailsOptions
  displayViewButton?: boolean
  isActive?: boolean
  light?: boolean
  displayTripSteps?: boolean
  oneWayTripBookingStepId?: number
  returnTripBookingStepId?: number
  onTripStepChange?: (data: TripStepChangeData) => void
}

type TripStepChangeData = {
  tripBookingId: number
  oneWayTripBookingStepId?: number
  returnTripBookingStepId?: number
}

type TripBookingDetailsBadgeProps = {
  tripBooking: TripBookingTypes.Partial
  directionality: TripStepTypeEnum
  displayViewButton?: boolean

  isActive?: boolean

  className?: string
  detailOptions?: TripBookingLabelDetailsOptions
  oneWayTripBookingStepId?: number
  returnTripBookingStepId?: number
  displayTripSteps?: boolean

  onClick?: () => void
  onTripStepChange?: (data: TripStepChangeData) => void
}

function ControlledTripStepOption({
  tripStep,
  index,
  selectedTripStepId,
  displayInput = true,
  onChange
}: ControlledTripStepOptionProps) {
  const { id } = tripStep
  const { departurePlace, arrivalPlace, departureTime, arrivalTime } =
    TripStepProperties.getTripStepDetails(tripStep)

  return (
    <div className={'flex flex-wrap items-center gap-1'}>
      {displayInput && (
        <ControlledRadioInput
          label={`Etape ${index} : `}
          checked={selectedTripStepId === id}
          onCheck={() => onChange?.(id)}
        />
      )}
      <Typography
        variant={'label-medium'}
      >{`${departurePlace} - ${arrivalPlace} : Départ`}</Typography>
      <Badge variant={'grey'} text={departureTime} />
      <Typography variant={'label-medium'}>{` - Arrivée`}</Typography>
      <Badge variant={'grey'} text={arrivalTime} />
    </div>
  )
}

type TripStepItemProps = {
  data: TripStepTypes.Light[]
  selectedId?: number
  onChange?: (tripStepId: number) => void
}

function TripStepItem({ data, selectedId, onChange }: TripStepItemProps) {
  return data.map((tripStep, index) => (
    <ControlledTripStepOption
      key={tripStep.id}
      tripStep={tripStep}
      index={index + 1}
      onChange={onChange}
      selectedTripStepId={selectedId}
      displayInput={data.length > 1}
    />
  ))
}

function TripBookingTripSteps({
  tripBooking,
  oneWayTripBookingStepId,
  returnTripBookingStepId,
  onTripStepChange
}: {
  tripBooking: TripBookingTypes.Basic
  oneWayTripBookingStepId?: number
  returnTripBookingStepId?: number
  onTripStepChange?: (data: TripStepChangeData) => void
}) {
  const { id, isTakeInCharge, directionality } = tripBooking
  const isOneWay = directionality === TransportTypeEnum.ONE_WAY
  const tripSteps = TripBookingProperties.getAllTripSteps(tripBooking)
  const roundTripOneWayTripSteps = TripBookingProperties.getTripStepsByDirectionality(
    TripStepTypeEnum.INBOUND,
    tripBooking
  )
  const roundTripReturnTripSteps = TripBookingProperties.getTripStepsByDirectionality(
    TripStepTypeEnum.OUTBOUND,
    tripBooking
  )

  if (isTakeInCharge) return null

  if (tripSteps && (isOneWay || directionality === TransportTypeEnum.RETURN))
    return (
      <TripStepItem
        data={tripSteps}
        selectedId={isOneWay ? oneWayTripBookingStepId : returnTripBookingStepId}
        onChange={(tripStepId) =>
          onTripStepChange?.({
            tripBookingId: id,
            oneWayTripBookingStepId: isOneWay ? tripStepId : undefined,
            returnTripBookingStepId: isOneWay ? undefined : tripStepId
          })
        }
      />
    )

  return (
    <>
      {roundTripOneWayTripSteps && (
        <>
          {roundTripReturnTripSteps && (
            <Typography variant={'label-semibold'} color={tailwindColors.primary.DEFAULT}>
              Aller
            </Typography>
          )}

          <TripStepItem
            data={roundTripOneWayTripSteps}
            selectedId={oneWayTripBookingStepId}
            onChange={(tripStepId) =>
              onTripStepChange?.({
                tripBookingId: id,
                oneWayTripBookingStepId: tripStepId,
                returnTripBookingStepId
              })
            }
          />
        </>
      )}

      {roundTripReturnTripSteps && (
        <>
          {roundTripOneWayTripSteps && (
            <Typography variant={'label-semibold'} color={tailwindColors.primary.DEFAULT}>
              Retour
            </Typography>
          )}

          <TripStepItem
            data={roundTripReturnTripSteps}
            selectedId={returnTripBookingStepId}
            onChange={(tripStepId) =>
              onTripStepChange?.({
                tripBookingId: id,
                returnTripBookingStepId: tripStepId,
                oneWayTripBookingStepId
              })
            }
          />
        </>
      )}
    </>
  )
}

export function TripBookingItem({
  type,
  tripBooking,
  options: detailOptions,
  displayViewButton = false,
  isActive = false,
  displayTripSteps = false,
  directionality: directionalityProp,
  oneWayTripBookingStepId,
  returnTripBookingStepId,
  onTripStepChange
}: TripBookingItemProps) {
  const tripBookingInfo = tripBookingTypeMapping[type]
  const { secondaryReservation } = tripBooking
  const tripBookingId = type === 'main' ? tripBooking.id : secondaryReservation?.id
  const directionality =
    type === 'main' ? tripBooking.directionality : secondaryReservation?.directionality
  const tripBookingDetails = TripDateProperties.getTripBookingDetails(
    tripBooking,
    detailOptions,
    directionalityProp
  )
  const details = tripBookingDetails?.[type === 'main' ? 'mainDetails' : 'secondaryDetails']
  const directionalityInfo = TripBookingProperties.getDirectionalityDetails(directionality)
  const targetTripBooking = type === 'main' ? tripBooking : secondaryReservation

  const hasSecondaryTripBooking = !!secondaryReservation

  return (
    <>
      <div className={'flex flex-row gap-2 items-center justify-between w-full'}>
        <Typography variant={'label-semibold'} className={'h-auto text-wrap'}>
          {/* only display the label when we have a secondary trip booking */}
          {(detailOptions?.displayLabel || hasSecondaryTripBooking) && (
            <Typography
              as="span"
              variant={'label-semibold'}
              className={'w-fit inline'}
              color={tripBookingInfo.value}
            >
              {`${tripBookingInfo.label} : `}
            </Typography>
          )}
          <Typography
            as="span"
            variant={'label-semibold'}
            className={'w-fit inline'}
            color={directionalityInfo?.textColor}
          >
            {`${directionalityInfo?.label} `}
          </Typography>
          - {details}
        </Typography>

        {displayViewButton && tripBookingId && (
          <ViewTripBookingButton tripBookingId={tripBookingId} isActive={isActive} />
        )}
      </div>

      {displayTripSteps && targetTripBooking && (
        <TripBookingTripSteps
          tripBooking={targetTripBooking}
          onTripStepChange={onTripStepChange}
          oneWayTripBookingStepId={oneWayTripBookingStepId}
          returnTripBookingStepId={returnTripBookingStepId}
        />
      )}
    </>
  )
}

/**
 * Badge to display the details of a trip booking
 * @param tripBooking - The trip booking to display the details of
 * @returns A badge to display the details of a trip booking
 * @example
 *
 * ```tsx
 *    <TripBookingDetailsBadge tripBooking={tripBooking} />
 * ```
 */
export function TripBookingDetailsBadge({
  tripBooking,
  displayViewButton = false,
  oneWayTripBookingStepId,
  returnTripBookingStepId,
  className,
  isActive = false,
  directionality,
  detailOptions = { count: ['reserved-allocated'] },
  displayTripSteps = false,
  onClick,
  onTripStepChange
}: TripBookingDetailsBadgeProps) {
  // create a common props for the trip booking item
  const tripBookingItemValues: Omit<TripBookingItemProps, 'type'> = {
    tripBooking,
    directionality,
    options: detailOptions,
    displayViewButton,
    displayTripSteps,
    oneWayTripBookingStepId,
    returnTripBookingStepId,
    onTripStepChange
  }

  return (
    <div
      onClick={onClick}
      className={cn(
        'bg-white-hover px-3 py-2 rounded border border-grey-light flex-wrap flex gap-2 min-w-96',
        { 'bg-primary-light border-primary-light-active': isActive },
        className
      )}
    >
      {tripBooking && (
        <div className="flex flex-col gap-1">
          <TripBookingItem type={'main'} {...tripBookingItemValues} isActive={isActive} />
        </div>
      )}

      {tripBooking?.secondaryReservation && (
        <div className="flex flex-col gap-1">
          <TripBookingItem type={'secondary'} {...tripBookingItemValues} isActive={isActive} />
        </div>
      )}
    </div>
  )
}

type ControlledTripBookingDetailsBadgeProps = Omit<
  TripBookingDetailsBadgeProps,
  'mainTripSteps' | 'secondaryTripSteps'
>

TripBookingDetailsBadge.Controlled = (props: ControlledTripBookingDetailsBadgeProps) => {
  return (
    <FormItem>
      <FormControl>
        <TripBookingDetailsBadge {...props} displayTripSteps />
      </FormControl>
    </FormItem>
  )
}

type TripBookingTabsProps = {
  tripType: TripTypeEnum
  tripBookings?: TripBookingTypes.Partial[]
  activeTab: number // starts from 0
  directinality: TripStepTypeEnum
  onTabChange?: (tabIndex: number) => void
  onTripBookingChange?: (tripBooking: TripBookingTypes.Partial) => void
}

export function TripBookingTabs({
  tripType,
  tripBookings,
  activeTab,
  directinality,
  onTabChange,
  onTripBookingChange
}: TripBookingTabsProps) {
  return (
    <div className={'flex flex-wrap w-fit gap-7 border border-grey-light rounded-lg py-3 px-2'}>
      {tripBookings?.map((tripBooking, index) => (
        <TripBookingDetailsBadge
          key={tripBooking.id}
          directionality={directinality}
          tripBooking={tripBooking}
          onClick={() => {
            onTabChange?.(index)
            onTripBookingChange?.(tripBooking)
          }}
          detailOptions={{
            count:
              tripType === TripTypeEnum.PRIMARY
                ? ['allocated-dispatched']
                : ['reserved-dispatched']
          }}
          className={cn('cursor-pointer max-w-96', {
            'bg-transparent border-transparent': index !== activeTab
          })}
        />
      ))}
    </div>
  )
}

type TripBookingDetailsItemProps = {
  type?: TripBookingType
  tripBooking: TripBookingTypes.Partial
  directionality: TripStepTypeEnum
}

function TripBookingDetailsItem({
  type,
  tripBooking,
  directionality
}: TripBookingDetailsItemProps) {
  const tripSteps = TripBookingProperties.getAllTripSteps(tripBooking, directionality)
  const tripBookingInfo = type ? tripBookingTypeMapping[type] : undefined
  const mainTripStep = TripBookingProperties.getTripStep(tripBooking)
  const mainDepartureTime = tripSteps?.length
    ? DateFormatter.HHhMM(DateFormatter.createDate(tripSteps?.[0].departureTime))
    : undefined
  const mainArrivalTime = tripSteps?.length
    ? DateFormatter.HHhMM(DateFormatter.createDate(tripSteps?.[tripSteps.length - 1].arrivalTime))
    : undefined

  function TripStepRow({
    label,
    tripStep,
    color,
    ...props
  }: {
    label: string | ReactNode
    tripStep: TripStepTypes.Light
    color?: TailwindColor
    details?: string
    departureTime?: string
    arrivalTime?: string
  }) {
    const { departurePlace, arrivalPlace, departureTime, arrivalTime } =
      TripStepProperties.getTripStepDetails(tripStep)
    return (
      <div className={'flex-row flex gap-2 items-center'}>
        {typeof label === 'string' ? (
          <Typography variant={'label-medium'} color={color}>
            {label}
          </Typography>
        ) : (
          label
        )}

        <Typography variant={'label-medium'}>
          {props.details ?? `${departurePlace} - ${arrivalPlace}`}
        </Typography>
        <Typography variant={'label-medium'}>Départ</Typography>
        <Badge variant={'grey'} size={'xsmall'} text={props.departureTime ?? departureTime} />
        <Typography variant={'label-medium'}>{` - Arrivée`}</Typography>
        <Badge variant={'grey'} size={'xsmall'} text={props.arrivalTime ?? arrivalTime} />
      </div>
    )
  }

  function TripProviderRow({ tripProvider }: { tripProvider: DepartureTimeTripProvider }) {
    const { label, meetingPlace, time } = tripProvider
    return (
      <div className={'flex-row flex gap-2 items-center'}>
        <Typography variant={'label-medium'}>
          {label}
          {meetingPlace ? ` (RDV : ${meetingPlace})` : ''}
        </Typography>
        <Badge
          variant={'grey'}
          size={'xsmall'}
          text={DateFormatter.HHhMM(DateFormatter.createDate(time))}
        />
      </div>
    )
  }

  function getMainTripStepDetails() {
    const details: string[] = []
    const { transportMeanNumber, carNumber } = mainTripStep ?? {}

    if (!tripBooking.isTakeInCharge) {
      // add the reservation number
      details.push(tripBooking.reservationNumber)

      // add the transport mean name and number
      if (transportMeanNumber)
        details.push(
          `${getTranslatedName(tripBooking.transportMeans)} - N° de vol/train : ${transportMeanNumber}`
        )
    }
    // add the car number
    if (carNumber) details.push(`N° de voiture : ${carNumber}`)

    return details.join(' - ')
  }

  if (tripBooking.isTakeInCharge) {
    return (
      <div className={'flex flex-wrap gap-2 items-center'}>
        <Typography variant={'label-semibold'} color={tailwindColors.primary.DEFAULT}>
          Prise en charge :
        </Typography>

        <Typography variant={'label-semibold'}>{tripBooking.label}</Typography>

        <Typography variant={'label-semibold'}>RDV Parents</Typography>

        {tripBooking.meetingTimeForParents && (
          <Badge
            variant={'grey'}
            size={'xsmall'}
            text={DateFormatter.HHhMM(DateFormatter.createDate(tripBooking.meetingTimeForParents))}
          />
        )}
      </div>
    )
  }

  if (!mainTripStep) return null
  return (
    <div className={'flex flex-col gap-2'}>
      <TripStepRow
        tripStep={mainTripStep}
        label={
          <div className={'flex-row gap-1 flex'}>
            {tripBookingInfo && (
              <Typography variant={'label-semibold'} color={tripBookingInfo.value}>
                {tripBookingInfo.label}
              </Typography>
            )}

            <Typography variant={'label-semibold'} color={tailwindColors.primary.DEFAULT}>
              Réservation :
            </Typography>
          </div>
        }
        details={getMainTripStepDetails()}
        arrivalTime={mainArrivalTime}
        departureTime={mainDepartureTime}
      />

      {tripSteps?.map((tripStep, index) => (
        <div key={index} className={'flex flex-col gap-2'}>
          {/* display trip step  */}
          <TripStepRow tripStep={tripStep} label={`Etape ${index + 1} :`} />

          {/* display trip provider */}
          {tripStep.departureTimeTripProviders &&
            tripStep.departureTimeTripProviders.length > 0 && (
              <div className={'flex flex-row gap-2 items-center'}>
                <Typography variant={'label-semibold'} color={tailwindColors.primary.DEFAULT}>
                  RDV prestataire(s) :
                </Typography>
                <div className={'flex flex-col gap-2'}>
                  {tripStep.departureTimeTripProviders.map((tripProvider, index) => (
                    <TripProviderRow key={index} tripProvider={tripProvider} />
                  ))}
                </div>
              </div>
            )}
        </div>
      ))}
    </div>
  )
}

export function TripBookingDetails({
  tripBooking,
  directionality
}: {
  tripBooking?: TripBookingTypes.Partial
  directionality: TripStepTypeEnum
}) {
  if (!tripBooking) return
  const { secondaryReservation } = tripBooking

  return (
    <div className={'bg-white-hover border border-grey-light p-3 flex flex-col rounded-lg gap-2'}>
      <Typography variant={'tag'}>Détails réservation</Typography>

      <div className={'flex flex-wrap gap-6'}>
        <TripBookingDetailsItem
          type={secondaryReservation ? 'main' : undefined}
          tripBooking={tripBooking}
          directionality={directionality}
        />

        {secondaryReservation && (
          <TripBookingDetailsItem
            type={'secondary'}
            tripBooking={secondaryReservation}
            directionality={directionality}
          />
        )}
      </div>
    </div>
  )
}

type TripBookingAllocationsTableProps = Pick<
  DataTableProps<TripPlaceToCenterRequestPartial, TripPlaceToCenterRequestPartial>,
  'columns'
> & {
  directionality: TripStepTypeEnum
  data?: TripPlaceToCenterRequestPartial[]
}

export function TripBookingAllocationsTable(props: TripBookingAllocationsTableProps) {
  const { columns, data, directionality } = props

  const rows =
    data
      ?.map((request) => ({
        ...request,
        allocatedPlaces: request.allocatedPlaces.filter(
          (place) => directionality === place.directionality
        )
      }))
      .filter((request) => request.allocatedPlaces.length > 0) ?? []

  if (!rows || rows.length === 0) return null
  return (
    <div className="flex flex-col gap-2 px-5">
      <TripDateDirectionalityBadge directionality={directionality} />

      <DataTable<TripPlaceToCenterRequestPartial>
        columns={columns}
        disableHover
        data={rows}
        tableClassName="!rounded-lg"
        headerClassName="last:!rounded-tr-lg first:!rounded-tl-lg"
        rowCount={rows.length}
        paginated={false}
      />
    </div>
  )
}
